import {
    createPlanogramProduct,
    normalizeWeightOrVolume,
    PlanogramProductModel,
} from './PlanogramProductModel';

const convertDateFromApiFormat = (
    value: unknown,
): Date | null =>
    value
        ? value instanceof Date
            ? value
            : new Date(value as string)
        : null;

export class PlanogramCellModel {
    [key: string]: unknown;

    constructor(args: Record<string, unknown>) {
        const {
            id,
            type,
            product,
            price,
            minQuantity,
            maxQuantity,
            mergeCells,
            categoryColor,
            currency,
            merged,
            mergeData,
            category,
            mdbId,
            evaDtsId,
            effectiveDate,
            externalId,
            mergedMdbIds,
            mergedEvaDtsIds,
            weightOrVolume,
            weightVolume,
            unitOfMeasurement,
        } = args;

        const normalizedWeightOrVolume =
            normalizeWeightOrVolume(
                weightOrVolume,
                weightVolume,
                unitOfMeasurement,
            );

        this.id = id;
        this.type = type || 'empty';
        this.product = product || null;
        this.price = price ?? null;
        this.minQuantity = minQuantity ?? null;
        this.maxQuantity = maxQuantity ?? null;
        this.categoryColor = categoryColor || '';
        this.currency = currency || null;
        this.mergeCells = mergeCells ?? null;
        this.merged = merged === true;
        this.mergeData = mergeData ?? null;
        this.category = category ?? null;
        this.mdbId = mdbId ?? null;
        this.evaDtsId = evaDtsId ?? null;
        this.externalId = externalId ?? null;
        this.mergedMdbIds = mergedMdbIds ?? null;
        this.mergedEvaDtsIds =
            mergedEvaDtsIds ?? null;

        /*
         * New planogram API representation.
         */
        this.weightOrVolume =
            normalizedWeightOrVolume;

        /*
         * Legacy UI representation.
         */
        this.weightVolume =
            normalizedWeightOrVolume?.value ??
            weightVolume ??
            null;

        this.unitOfMeasurement =
            normalizedWeightOrVolume?.unit ??
            unitOfMeasurement ??
            null;

        this.effectiveDate = effectiveDate
            ? effectiveDate instanceof Date
                ? effectiveDate
                : new Date(effectiveDate as string)
            : null;
    }

    static fromFormData(
        args: Record<string, unknown>,
    ): PlanogramCellModel {
        const {
            rowIndex,
            colIndex,
            formData,
            prevCell,
        } = args as {
            rowIndex: number;
            colIndex: number;
            formData: Record<string, any>;
            prevCell: Record<string, any>;
        };

        const id = `${rowIndex}-${colIndex}`;
        const product =
            createPlanogramProduct(formData);

        const legacyWeightVolume =
            formData.weightVolume ??
            formData.product?.weightVolume ??
            null;

        const legacyUnitOfMeasurement =
            formData.unitOfMeasurement ??
            formData.product?.unitOfMeasurement ??
            null;

        const weightOrVolume =
            normalizeWeightOrVolume(
                formData.weightOrVolume ??
                    formData.product?.weightOrVolume,
                legacyWeightVolume,
                legacyUnitOfMeasurement,
            );

        return new PlanogramCellModel({
            id,
            type: 'product',
            product,
            price: formData.price,
            minQuantity: formData.minQuantity,
            maxQuantity: formData.maxQuantity,
            mergeCells: formData.mergeCells,
            categoryColor:
                formData.product?.categoryColor,
            currency: formData.currency,
            merged:
                prevCell?.merged || false,
            mergeData:
                prevCell?.mergeData || null,
            category:
                prevCell?.category || null,
            mdbId:
                formData.mdbId === undefined ||
                formData.mdbId === null
                    ? prevCell?.mdbId ?? null
                    : formData.mdbId,
            evaDtsId:
                formData.evaDtsId === undefined ||
                formData.evaDtsId === null
                    ? prevCell?.evaDtsId ?? null
                    : formData.evaDtsId,
            effectiveDate:
                formData.effectiveDate ?? null,
            externalId:
                formData.externalId !== undefined
                    ? formData.externalId
                    : prevCell?.externalId ?? null,
            weightOrVolume,
            weightVolume:
                weightOrVolume?.value ??
                legacyWeightVolume,
            unitOfMeasurement:
                weightOrVolume?.unit ??
                legacyUnitOfMeasurement,
        });
    }

    static fromApiCell(
        args: Record<string, unknown>,
    ): PlanogramCellModel {
        const {
            apiCell,
            rowIndex,
            colIndex,
        } = args as {
            apiCell: Record<string, any>;
            rowIndex: number;
            colIndex: number;
        };

        const id = `${rowIndex}-${colIndex}`;

        const product =
            PlanogramProductModel.fromApiCell(
                apiCell,
            );

        const weightOrVolume =
            normalizeWeightOrVolume(
                apiCell?.weightOrVolume ??
                    apiCell?.weight_or_volume,
                apiCell?.weightVolume ??
                    apiCell?.weight_volume,
                apiCell?.unitOfMeasurement ??
                    apiCell?.unit_of_measurement,
            );

        return new PlanogramCellModel({
            id,
            type:
                apiCell?.productExternalId == null
                    ? 'empty'
                    : 'product',
            product,
            price: apiCell?.price,
            minQuantity:
                apiCell?.minQuantity,
            maxQuantity:
                apiCell?.maxQuantity,
            mergeCells: null,
            categoryColor:
                apiCell?.categoryColor || '',
            currency:
                apiCell?.currency,
            merged: false,
            mergeData: null,
            category: null,
            mdbId:
                apiCell?.mdbId ?? null,
            evaDtsId:
                apiCell?.evaDtsId ?? null,
            mergedMdbIds:
                apiCell?.mergedMdbIds ?? null,
            mergedEvaDtsIds:
                apiCell?.mergedEvaDtsIds ?? null,
            effectiveDate:
                convertDateFromApiFormat(
                    apiCell?.effectiveDate,
                ),
            externalId:
                apiCell?.externalId ?? null,
            weightOrVolume,
            weightVolume:
                weightOrVolume?.value ?? null,
            unitOfMeasurement:
                weightOrVolume?.unit ?? null,
        });
    }

    static empty(args: {
        rowIndex: number;
        colIndex: number;
    }): PlanogramCellModel {
        const {
            rowIndex,
            colIndex,
        } = args;

        const id = `${rowIndex}-${colIndex}`;

        return new PlanogramCellModel({
            id,
            type: 'empty',
            product: null,
            price: null,
            minQuantity: null,
            maxQuantity: null,
            mergeCells: null,
            categoryColor: '',
            currency: null,
            merged: false,
            mergeData: null,
            category: null,
            mdbId: null,
            evaDtsId: null,
            effectiveDate: null,
            externalId: null,
            weightOrVolume: null,
            weightVolume: null,
            unitOfMeasurement: null,
        });
    }

    copyProductData(
        sourceCell: Record<string, unknown>,
    ): this {
        if (
            sourceCell.type === 'product' &&
            sourceCell.product
        ) {
            const weightOrVolume =
                normalizeWeightOrVolume(
                    sourceCell.weightOrVolume,
                    sourceCell.weightVolume,
                    sourceCell.unitOfMeasurement,
                );

            this.type = 'product';
            this.product = sourceCell.product;
            this.price = sourceCell.price;
            this.minQuantity =
                sourceCell.minQuantity;
            this.maxQuantity =
                sourceCell.maxQuantity;
            this.categoryColor =
                sourceCell.categoryColor;
            this.currency = sourceCell.currency;
            this.category = sourceCell.category;

            this.weightOrVolume =
                weightOrVolume;

            this.weightVolume =
                weightOrVolume?.value ??
                sourceCell.weightVolume ??
                null;

            this.unitOfMeasurement =
                weightOrVolume?.unit ??
                sourceCell.unitOfMeasurement ??
                null;
        }

        /*
         * Always copied, regardless of the cell type.
         */
        this.mdbId = sourceCell.mdbId;
        this.evaDtsId = sourceCell.evaDtsId;
        this.externalId = sourceCell.externalId;
        this.effectiveDate =
            sourceCell.effectiveDate;

        return this;
    }

    resetToEmpty(): this {
        this.type = 'empty';
        this.product = null;
        this.price = null;
        this.minQuantity = null;
        this.maxQuantity = null;
        this.categoryColor = null;
        this.currency = null;
        this.category = null;
        this.effectiveDate = null;
        this.weightOrVolume = null;
        this.weightVolume = null;
        this.unitOfMeasurement = null;

        /*
         * mdbId, evaDtsId and externalId are intentionally preserved.
         */
        return this;
    }

    resetMergeData(): this {
        this.merged = false;
        this.mergeData = null;

        return this;
    }
}

export const createPlanogramCell = (
    args: Record<string, unknown>,
): PlanogramCellModel =>
    PlanogramCellModel.fromFormData(args);

export const createEmptyCell = (args: {
    rowIndex: number;
    colIndex: number;
}): PlanogramCellModel =>
    PlanogramCellModel.empty(args);